import {
    formatDistance,
    getBreedCharacteristics,
    getDogAgeDisplayVerbose,
    getDogSexDisplayFromAvailability,
} from "@wags/catalog/pure.ts";
import { buildTransportModel } from "./transport";
import { type CollectionWagsListing, formatBreedNames } from "./wags-listings";
import { getListingSizeDisplay } from "./listing-size";
import type { ListingData } from "./listing-section-data";
import { formatMoneyRange, priceFormatter } from "@/lib/ui/price";

export type ListingSectionContext = {
    distanceMiles: number | null;
    mapSrc: string | undefined;
    seekerState: string | undefined;
    fitPercent: number | null;
    fitLine: string | undefined;
};

function listingCity(
    org: CollectionWagsListing["organization"],
): string | undefined {
    if (org?.city && org.state) return `${org.city}, ${org.state}`;
    return org?.city ?? undefined;
}

function listingGoodWith(dog: CollectionWagsListing): string[] | undefined {
    const goodWith = [
        dog.isGoodWithKids ? "Kids" : null,
        dog.isGoodWithDogs ? "Dogs" : null,
        dog.isGoodWithCats ? "Cats" : null,
    ].filter((value): value is string => value != null);
    return goodWith.length > 0 ? goodWith : undefined;
}

function listingCoat(
    breed: CollectionWagsListing["breeds"][number] | undefined,
): string | undefined {
    if (!breed?.coatType) return undefined;
    return breed.coatType.charAt(0).toUpperCase() + breed.coatType.slice(1);
}

function listingTransportRows(dog: CollectionWagsListing) {
    if (dog.transports.length === 0) return undefined;
    return dog.transports.map((transport) => ({
        label:
            transport.label ??
            (transport.type === "delivery"
                ? "Delivery available"
                : "Pickup available"),
    }));
}

function listingIdentity(dog: CollectionWagsListing) {
    return {
        dogName: dog.title?.trim() || "This pup",
        breedName: formatBreedNames(dog) || undefined,
        ageLabel:
            getDogAgeDisplayVerbose(dog.dob, "") || dog.ageLabel || undefined,
        sexLabel:
            getDogSexDisplayFromAvailability(
                dog.numberOfMalesAvailable,
                dog.numberOfFemalesAvailable,
                "",
            ) || undefined,
    };
}

function listingAvg(
    breedName: string | undefined,
    averagePrice: number | null,
) {
    const avgLabel = breedName ? `Typical ${breedName}` : undefined;
    return {
        avgLabel,
        avgPrice:
            avgLabel && averagePrice != null
                ? priceFormatter.format(averagePrice)
                : undefined,
    };
}

function listingPhotos(media: CollectionWagsListing["media"]) {
    return media
        .map((item) => item.url)
        .filter((url): url is string => Boolean(url));
}

function listingProvider(dog: CollectionWagsListing) {
    return {
        providerName: dog.organization?.name ?? undefined,
        providerId: dog.organizationId ?? undefined,
        providerIsVerified: dog.organization?.isVerified ?? false,
    };
}

function listingHealthTests(tests: string[]) {
    return tests.length > 0 ? tests : undefined;
}

function listingDescription(description: string | null) {
    return description?.trim() || undefined;
}

export function listingSectionData(
    dog: CollectionWagsListing,
    context: ListingSectionContext,
): ListingData {
    const breed = dog.breeds[0];
    const size = getListingSizeDisplay({
        listingWeightBucket: dog.weightBucket,
        listingWeightMinLbs: dog.weightMinLbs,
        listingWeightMaxLbs: dog.weightMaxLbs,
        breedSize: breed?.size,
        breedWeightMinLbs: breed?.weightMinLbs,
        breedWeightMaxLbs: breed?.weightMaxLbs,
    });
    const rowsOut = breed ? getBreedCharacteristics(breed) : [];

    return {
        ...listingIdentity(dog),
        sizeLabel: size.sizeLabel,
        sizeFromBreed:
            dog.weightBucket == null &&
            dog.weightMinLbs == null &&
            dog.weightMaxLbs == null,
        weightRange: size.weightRange,
        exercise: characteristic(rowsOut, "Exercise"),
        lifespan: characteristic(rowsOut, "Lifespan"),
        trainability: characteristic(rowsOut, "Trainability"),
        livingSpace: characteristic(rowsOut, "Living space"),
        goodWith: listingGoodWith(dog),
        city: listingCity(dog.organization),
        coat: listingCoat(breed),
        mapSrc: context.mapSrc,
        distanceLabel:
            formatDistance(context.distanceMiles, "narrative") ?? undefined,
        distanceMiles: context.distanceMiles ?? undefined,
        priceLabel: formatMoneyRange(dog.priceRange),
        ...listingAvg(breed?.name, dog.averagePrice),
        photos: listingPhotos(dog.media),
        fitPercent: context.fitPercent,
        fitLine: context.fitLine,
        ...listingProvider(dog),
        healthTests: listingHealthTests(dog.healthTests),
        transports: listingTransportRows(dog),
        transportModel: buildTransportModel({
            transports: dog.transports,
            isGroundTransportAvailable: dog.isGroundTransportAvailable,
            isFlightNannyTransportAvailable:
                dog.isFlightNannyTransportAvailable,
            isAirCargoTransportAvailable: dog.isAirCargoTransportAvailable,
            seekerState: context.seekerState,
        }),
        description: listingDescription(dog.description),
        descriptionQa: dog.descriptionQa,
    };
}

function characteristic(
    rows: Array<{ label: string; value: string }>,
    label: string,
): string | undefined {
    return rows.find((r) => r.label === label)?.value;
}
